import * as PropTypes from 'prop-types'
import React, {PureComponent} from 'react'
import {HighLighter} from './components/highlighter'
import {colors} from '../../../../util/colors'
import {markHighlights} from './utils'

const HighlightsContext = React.createContext({
  source: null,
})

class HighlightsProvider extends PureComponent {
  static propTypes = {
    source: PropTypes.string,
    children: PropTypes.node.isRequired,
  }

  static defaultProps = {
    source: null,
  }

  constructor(props) {
    super(props)

    this.state = {
      sourceHighlights: [],
      targetHighlights: [],
      registerHighlighters: [],
    }
  }

  setHighlights = (target, id) => {
    const {source} = this.props
    const highlights = markHighlights(source, target)
    if (highlights) {
      // set highlights for source and target
      this.setState(prevState => {
        const colorIndex = prevState.sourceHighlights.length + 1
        return {
          ...prevState,
          sourceHighlights: [
            ...prevState.sourceHighlights,
            {...highlights?.sourceHighlights, color: colors[colorIndex], targetId: id},
          ],
          targetHighlights: [
            ...prevState.targetHighlights,
            {...highlights?.targetHighlights, color: colors[colorIndex], id},
          ],
        }
      })
    }
  }

  static getTargetHighlight = (highlights, ident) => highlights.find(({id}) => id === ident)

  static getSourceHighlight = highlights => {
    // pass only the longest matching string
    const highlight = highlights.reduce(
      (prev, current) => (prev.end - prev.start > current.end - current.start ? prev : current),
      []
    )
    return highlight
  }

  registerHighlighter = ident => {
    this.setState(prevState => ({
      ...prevState,
      registerHighlighters: [...prevState.registerHighlighters, ident],
    }))
  }

  unregisterHighlighter = ident => {
    // when unregistering filter out source highlights
    this.setState(prevState => {
      const highlights = [...prevState.sourceHighlights.filter(({targetId}) => targetId !== ident)]
      return {
        ...prevState,
        registerHighlighters: prevState.registerHighlighters.filter(id => id !== ident),
        sourceHighlights: highlights,
      }
    })
  }

  render() {
    const {source, children} = this.props
    const {sourceHighlights, targetHighlights} = this.state

    const mergedValue = {
      source,
      sourceHighlights,
      targetHighlights,
      getTargetHighlight: this.getTargetHighlight,
      getSourceHighlight: this.getSourceHighlight,
      registerHighlighter: this.registerHighlighter,
      unregisterHighlighter: this.unregisterHighlighter,
      setHighlights: this.setHighlights,
    }
    return <HighlightsContext.Provider value={mergedValue}>{children}</HighlightsContext.Provider>
  }
}

export const TargetHighlighterContainer = props => (
  <HighlightsContext.Consumer>
    {({
      getTargetHighlight,
      registerHighlighter,
      unregisterHighlighter,
      targetHighlights,
      setHighlights,
    }) => (
      <HighLighter
        getHighlight={getTargetHighlight}
        registerHighlighter={registerHighlighter}
        unregisterHighlighter={unregisterHighlighter}
        highlights={targetHighlights}
        setHighlights={setHighlights}
        {...props}
      />
    )}
  </HighlightsContext.Consumer>
)

export const SourceHighlighterContainer = props => (
  <HighlightsContext.Consumer>
    {({getSourceHighlight, registerHighlighter, unregisterHighlighter, sourceHighlights}) => (
      <HighLighter
        getHighlight={getSourceHighlight}
        registerHighlighter={registerHighlighter}
        unregisterHighlighter={unregisterHighlighter}
        highlights={sourceHighlights}
        {...props}
      />
    )}
  </HighlightsContext.Consumer>
)

export default HighlightsProvider
